/**
 * Timezone utilities for handling date conversions between timezones
 */

import dayjs from 'dayjs';
import { parseDate } from './core';

/**
 * Formats a date string for local display with timezone consideration
 * 
 * @param dateString The date string to format
 * @param formatPattern The desired output format
 * @param timezone Optional timezone (defaults to local)
 * @returns A formatted localized date string
 */
export function formatLocaleDateString(
  dateString: string | null | undefined, 
  formatPattern: string = 'MMM D, YYYY h:mm A',
  timezone?: string
): string {
  if (!dateString) return 'No date';
  
  const date = parseDate(dateString);
  if (!date) return 'Invalid date';
  
  // If a specific timezone is provided, use it
  if (timezone) {
    return date.tz(timezone).format(formatPattern);
  }
  
  // Otherwise use the browser's local timezone
  return date.format(formatPattern);
}

/**
 * Formats a date string with the original timezone information preserved
 * This is useful when displaying dates that were input by users in different timezones
 * 
 * @param dateString The date string to format
 * @param formatPattern The desired output format
 * @param originalTimezone The timezone of the user who inputted the date
 * @returns A formatted date string in the original timezone
 */
export function formatDateWithOriginalTimezone(
  dateString: string | null | undefined,
  formatPattern: string = 'MMM D, YYYY h:mm A',
  originalTimezone?: string
): string {
  if (!dateString) return 'No date';
  
  const date = parseDate(dateString);
  if (!date) return 'Invalid date';
  
  // If original timezone is provided, use it
  if (originalTimezone) {
    return date.tz(originalTimezone).format(formatPattern);
  }
  
  // If the date string already has timezone info, preserve it
  // Otherwise fall back to UTC
  return date.format(formatPattern);
}

/**
 * Converts a UTC timestamp to the user's local time
 * @param utcDateString ISO timestamp in UTC format (e.g., "2025-03-11T19:37:19.146Z")
 * @param format Desired output format (default: "MM/DD/YYYY h:mm A")
 * @returns Formatted local date-time string
 */
export function formatToLocalTime(
  utcDateString: string | null | undefined, 
  format: string = 'MM/DD/YYYY h:mm A'
): string {
  if (!utcDateString) return 'No date';

  try {
    // First ensure we're treating the input as UTC
    // The .utc() method ensures we're starting with UTC time
    const utcDate = dayjs.utc(utcDateString);
    
    if (!utcDate.isValid()) {
      return 'Invalid date';
    }
    
    // Then convert to local time with the .local() method
    // This applies the correct UTC offset based on the user's timezone
    const localDate = utcDate.local();
    
    // Format using the specified format string
    return localDate.format(format);
  } catch (error) {
    return 'Invalid date';
  }
}

/**
 * Formats a database timestamp string for display in local time
 * 
 * @param dbTimestamp The timestamp string from the database (in UTC)
 * @param format The format string to use (defaults to 'MM/DD/YYYY HH:mm')
 * @returns A formatted date string in the user's local timezone
 */
export function formatDbTimestampToLocal(
  dbTimestamp: string | null, 
  format: string = 'MM/DD/YYYY HH:mm'
): string {
  if (!dbTimestamp) return '';
  
  try {
    // Parse the UTC timestamp first
    const utcDate = dayjs.utc(dbTimestamp);
    
    if (!utcDate.isValid()) {
      return '';
    }
    
    // Convert to local time zone
    const localDate = utcDate.local();
    const result = localDate.format(format);
    
    return result;
  } catch (error) {
    return '';
  }
}

/**
 * Formats a date to UTC for database storage while preserving the user's intended local time
 * 
 * @param date The Date object in local time
 * @returns String in UTC format with the correct timezone conversion
 */
export function formatDateToUTC(date: Date): string {
  if (!date) return null;
  
  // Simply use the built-in JavaScript conversion which correctly handles timezone offsets
  const utcISOString = date.toISOString();
  
  // Return the ISO string format required for the database
  return utcISOString;
}

/**
 * Converts a local date to UTC format for database storage
 * Always use this function when saving dates to the database
 * 
 * @param date The Date object in local time
 * @returns ISO string in UTC format (YYYY-MM-DDTHH:mm:ss.sssZ)
 */
export function formatLocalDateForStorage(date: Date): string {
  if (!date) return null;
  
  // Use the direct toISOString method which properly converts to UTC
  return date.toISOString();
}

/**
 * Creates a Date from separate date and time inputs
 * Both inputs are assumed to be in the local timezone
 * 
 * @param dateInput Date in YYYY-MM-DD format
 * @param timeInput Time in HH:MM format
 * @returns Combined Date object in local timezone
 */
export function createDateFromInputs(dateInput: string, timeInput: string): Date {
  if (!dateInput || !timeInput) return null;
  
  // Extract date parts from the date input (YYYY-MM-DD)
  const [year, month, day] = dateInput.split('-').map(part => parseInt(part, 10));
  
  // Extract time parts from the time input (HH:MM)
  const [hour, minute] = timeInput.split(':').map(part => parseInt(part, 10));
  
  // Create a date in the local timezone
  const localDate = new Date(year, month - 1, day, hour, minute);
  
  return localDate;
}
